import { Component } from '@opten/gin-tonic/helpers/compiler';

@Component({ className: 'js-mobilemenu', observe: true })
export class MobileMenuComponent {
	private static boundGlobal = false;
	private static isLoggedIn = false;
	private panel: HTMLElement | null;
	private panelBody: HTMLElement | null | undefined;
	private overlay: HTMLElement | null | undefined;
	private toggle: HTMLElement | null | undefined;
	private close: HTMLElement | null | undefined;
	private open = false;

	constructor(private element: HTMLElement) {
		// Resolve target panel by aria-controls on the toggle, allowing markup
		// where the panel may be outside the wrapper.
		this.toggle = this.element.querySelector<HTMLElement>(
			'.js-mobilemenu-toggle'
		);
		const controlsId = this.toggle?.getAttribute('aria-controls');
		this.panel =
			(controlsId
				? (document.getElementById(controlsId) as HTMLElement | null)
				: null) || this.element.querySelector<HTMLElement>('.c-mobilemenu');
		this.panelBody = this.panel?.querySelector<HTMLElement>(
			'.c-mobilemenu__panel'
		);
		this.overlay = this.panel?.querySelector<HTMLElement>(
			'.js-mobilemenu-overlay'
		);
		this.close = this.panel?.querySelector<HTMLElement>('.js-mobilemenu-close');

		this.toggle?.addEventListener('click', () => this.show());
		this.close?.addEventListener('click', () => this.hide());
		this.overlay?.addEventListener('click', () => this.hide());
		this.panel?.addEventListener('click', (e) => {
			const target = e.target as HTMLElement;
			const anchor = target.closest('a');
			// Only hide on normal links; ignore internal panel-switch links
			if (anchor && !anchor.closest('[data-mobilemenu-target]')) this.hide();
		});
		document.addEventListener('keydown', (e) => {
			if (this.open && e.key === 'Escape') this.hide();
		});

		// Close when resizing to desktop breakpoints
		window.addEventListener('resize', this.handleResize);

		// Bind a global handler once for in-panel navigation switches
		if (!MobileMenuComponent.boundGlobal) {
			document.addEventListener(
				'click',
				MobileMenuComponent.handleTargetLinkClick,
				{ capture: true }
			);
			// Global close handling for panels opened via switching
			document.addEventListener(
				'click',
				MobileMenuComponent.handleGlobalCloseClick,
				{ capture: true }
			);
			document.addEventListener(
				'keydown',
				MobileMenuComponent.handleGlobalKeydown
			);
			// Global action handler for auth-related actions
			document.addEventListener(
				'click',
				MobileMenuComponent.handleActionClick,
				{ capture: true }
			);
			// Initialize auth state on first bind
			//MobileMenuComponent.initAuthState().catch(() => {});
			MobileMenuComponent.boundGlobal = true;
		}
	}

	private show() {
		if (!this.panel) return;
		this.open = true;
		// ensure any previous closing state is cleared
		this.panel.classList.remove('is-closing');
		this.panel.hidden = false;
		this.panel.setAttribute('aria-hidden', 'false');
		this.toggle?.setAttribute('aria-expanded', 'true');
		this.lockScroll(true);
		// Force a reflow so the browser registers the initial transform state
		// before we add the .is-open class, ensuring the transition runs.
		void this.panel.offsetWidth;
		// Defer adding the class to the next frame for reliability after [hidden]
		requestAnimationFrame(() => {
			this.panel?.classList.add('is-open');
		});
	}

	private hide(keepScrollLocked = false) {
		if (!this.panel || !this.open) return;
		this.open = false;
		// Start closing: remove is-open to trigger slide-out, keep visible until end
		this.panel.classList.add('is-closing');
		this.panel.classList.remove('is-open');
		this.toggle?.setAttribute('aria-expanded', 'false');

		// Wait for the sliding transition to finish before hiding
		const duration = this.getTransitionMs();
		let done = false;
		const finish = () => {
			if (done) return;
			done = true;
			// Return focus to the toggle before hiding so aria-hidden isn't set while a descendant has focus
			if (
				this.panel &&
				document.activeElement &&
				this.panel.contains(document.activeElement)
			) {
				(this.toggle as HTMLElement | undefined)?.focus();
			}
			if (this.panel) {
				this.panel.classList.remove('is-closing');
				this.panel.setAttribute('aria-hidden', 'true');
				this.panel.hidden = true;
			}
			if (!keepScrollLocked) {
				this.lockScroll(false);
			}
			this.panelBody?.removeEventListener('transitionend', onEnd);
		};
		const onEnd = (e: Event) => {
			// Only react to transform transition on the panel body
			if ((e as TransitionEvent).propertyName !== 'transform') return;
			finish();
		};
		this.panelBody?.addEventListener('transitionend', onEnd);
		// Fallback in case transitionend doesn’t fire
		window.setTimeout(finish, duration + 50);
	}

	// Global delegated handler for links with data-mobilemenu-target to switch panels
	private static handleTargetLinkClick = (ev: Event) => {
		const targetEl = ev.target as HTMLElement;
		const link = targetEl?.closest(
			'[data-mobilemenu-target]'
		) as HTMLElement | null;
		if (!link) return;
		ev.preventDefault();
		ev.stopPropagation(); // prevent instance click handlers from unlocking scroll
		const id = link.getAttribute('data-mobilemenu-target');
		if (!id) return;
		const currentPanel = document.querySelector(
			'.c-mobilemenu.is-open'
		) as HTMLElement | null;
		const nextPanel = document.getElementById(id) as HTMLElement | null;
		if (!nextPanel) return;

		// Close current without unlocking scroll
		if (currentPanel) {
			currentPanel.classList.add('is-closing');
			currentPanel.classList.remove('is-open');
			const body = currentPanel.querySelector(
				'.c-mobilemenu__panel'
			) as HTMLElement | null;
			const duration = MobileMenuComponent.getTransitionMsFor(body);
			window.setTimeout(() => {
				currentPanel.classList.remove('is-closing');
				currentPanel.setAttribute('aria-hidden', 'true');
				(currentPanel as HTMLElement).hidden = true;
			}, duration + 50);
		}

		// Show the target panel
		nextPanel.classList.remove('is-closing');
		nextPanel.hidden = false;
		nextPanel.setAttribute('aria-hidden', 'false');
		// lock scroll (idempotent)
		document.documentElement.style.overflow = 'hidden';
		void nextPanel.offsetWidth;
		nextPanel.classList.add('is-open');
	};

	private static handleGlobalCloseClick = (ev: Event) => {
		const targetEl = ev.target as HTMLElement;
		const isCloseBtn = !!targetEl.closest(
			'.c-mobilemenu.is-open .js-mobilemenu-close'
		);
		const isOverlay = !!targetEl.closest(
			'.c-mobilemenu.is-open .js-mobilemenu-overlay'
		);
		if (!isCloseBtn && !isOverlay) return;
		ev.preventDefault();
		MobileMenuComponent.closeOpenPanel();
	};

	private static handleGlobalKeydown = (e: KeyboardEvent) => {
		if (e.key !== 'Escape') return;
		const openPanel = document.querySelector('.c-mobilemenu.is-open');
		if (!openPanel) return;
		MobileMenuComponent.closeOpenPanel();
	};

	// ==== Simple AJAX API for auth flows ====
	private static apiBase = '/umbraco/api';

	private static buildHeaders(): HeadersInit {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};
		const token = localStorage.getItem('access_token');
		if (token) headers['Authorization'] = `Bearer ${token}`;
		return headers;
	}

	private static setBusy(target: HTMLElement, busy: boolean) {
		const btn = target.closest('a,button') as HTMLElement | null;
		if (!btn) return;
		if (busy) {
			btn.setAttribute('aria-disabled', 'true');
			btn.classList.add('is-busy');
		} else {
			btn.removeAttribute('aria-disabled');
			btn.classList.remove('is-busy');
		}
	}

	private static showFeedback(
		panel: HTMLElement,
		message: string,
		isError = false
	) {
		const box = panel.querySelector(
			'.c-mobilemenu__feedback'
		) as HTMLElement | null;
		if (!box) return;
		box.textContent = message;
		box.classList.toggle('is-error', !!isError);
	}

	private static clearFeedback(panel: HTMLElement) {
		const box = panel.querySelector(
			'.c-mobilemenu__feedback'
		) as HTMLElement | null;
		if (!box) return;
		box.textContent = '';
		box.classList.remove('is-error');
	}

	private static getPanelById(id: string) {
		return document.getElementById(id) as HTMLElement | null;
	}

	private static readInputs(panel: HTMLElement): Record<string, string> {
		const values: Record<string, string> = {};
		panel
			.querySelectorAll<HTMLInputElement>('input.c-mobilemenu__input')
			.forEach((inp) => {
				if (inp.name) values[inp.name] = inp.value ?? '';
			});
		return values;
	}

	private static async postJson(path: string, payload: any) {
		const res = await fetch(`${MobileMenuComponent.apiBase}${path}`, {
			method: 'POST',
			headers: MobileMenuComponent.buildHeaders(),
			body: JSON.stringify(payload),
			credentials: 'same-origin',
		});
		return res;
	}

	private static async getJson(path: string) {
		const res = await fetch(`${MobileMenuComponent.apiBase}${path}`, {
			method: 'GET',
			headers: MobileMenuComponent.buildHeaders(),
			credentials: 'same-origin',
		});
		return res;
	}

	/*
	private static async initAuthState() {
		const hasTokens = !!localStorage.getItem('access_token');
		if (hasTokens) {
			try {
				const resp = await MobileMenuComponent.getJson('/Account/IsLoggedIn');
				if (resp.ok) {
					const is = await resp.json();
					MobileMenuComponent.isLoggedIn = !!is;
				} else {
					MobileMenuComponent.isLoggedIn = false;
				}
			} catch {
				MobileMenuComponent.isLoggedIn = false;
			}
			if (!MobileMenuComponent.isLoggedIn) {
				// Tokens present but server says not logged in
				localStorage.removeItem('access_token');
				localStorage.removeItem('refresh_token');
				MobileMenuComponent.showToast(
					'Sitzung abgelaufen, abgemeldet.',
					'error'
				);
			}
		} else {
			MobileMenuComponent.isLoggedIn = false;
		}
		MobileMenuComponent.applyLoggedInUi();

		// Route login clicks to logged-in panel if already logged in
		document.addEventListener(
			'click',
			(e) => {
				const t = e.target as HTMLElement;
				const loginToggle = t.closest(
					'[aria-controls="mobilemenu-client"], [data-mobilemenu-target="mobilemenu-client"]'
				) as HTMLElement | null;
				if (loginToggle && MobileMenuComponent.isLoggedIn) {
					e.preventDefault();
					e.stopPropagation();
					MobileMenuComponent.switchToPanel('mobilemenu-client-loggedin');
				}
			},
			{ capture: true }
		);
	}
	*/

	private static applyLoggedInUi() {
		// Hide the main mobile nav login link when logged in
		const loginLink = document.querySelector(
			'#mobilemenu .c-mobilemenu__link[data-mobilemenu-target="mobilemenu-client"]'
		) as HTMLElement | null;
		if (loginLink)
			loginLink.style.display = MobileMenuComponent.isLoggedIn ? 'none' : '';

		// Toggle header Kundenbereich icon state
		const customerToggle = document.querySelector(
			'button.c-header__toggle[aria-controls="mobilemenu-client"]'
		) as HTMLElement | null;
		if (customerToggle) {
			if (MobileMenuComponent.isLoggedIn) {
				customerToggle.classList.add('is-logged-in');
			} else {
				customerToggle.classList.remove('is-logged-in');
			}
		}
	}

	private static handleActionClick = async (ev: Event) => {
		const el = ev.target as HTMLElement;
		const actionEl = el.closest('[data-action]') as HTMLElement | null;
		if (!actionEl) return;
		const action = actionEl.getAttribute('data-action');
		if (!action) return;
		const panel = el.closest('.c-mobilemenu') as HTMLElement | null;
		if (!panel) return;
		ev.preventDefault();
		ev.stopPropagation();

		// no inline feedback; use toasts
		MobileMenuComponent.setBusy(actionEl, true);
		try {
			switch (action) {
				case 'login': {
					const values = MobileMenuComponent.readInputs(panel);
					const res = await MobileMenuComponent.postJson(
						'/Account/Authenticate',
						{
							Username: values['Username'] ?? values['Email'] ?? '',
							Password: values['Password'] ?? '',
						}
					);
					if (!res.ok) {
						const data = await MobileMenuComponent.tryReadJson(res);
						throw new Error(data?.message || 'Login fehlgeschlagen');
					}
					const data = await res.json();
					if (
						data?.result === 1 /* Succeeded */ &&
						data.accessToken &&
						data.refreshToken
					) {
						localStorage.setItem('access_token', data.accessToken);
						localStorage.setItem('refresh_token', data.refreshToken);
						MobileMenuComponent.isLoggedIn = true;
						MobileMenuComponent.applyLoggedInUi();
						MobileMenuComponent.showToast('Erfolgreich angemeldet.', 'success');
						// Switch to logged-in menu
						MobileMenuComponent.switchToPanel('mobilemenu-client-loggedin');
					} else {
						throw new Error('Login fehlgeschlagen');
					}
					break;
				}
				case 'logout': {
					const refresh = localStorage.getItem('refresh_token');
					if (refresh) {
						await MobileMenuComponent.postJson('/Account/RevokeRefreshToken', {
							RefreshToken: refresh,
						});
					}
					localStorage.removeItem('access_token');
					localStorage.removeItem('refresh_token');
					MobileMenuComponent.isLoggedIn = false;
					MobileMenuComponent.applyLoggedInUi();
					MobileMenuComponent.showToast('Abgemeldet.', 'info');
					MobileMenuComponent.switchToPanel('mobilemenu-client');
					break;
				}
				case 'forgot': {
					const values = MobileMenuComponent.readInputs(panel);
					await MobileMenuComponent.postJson('/Account/ForgotPassword', {
						Email: values['Email'] ?? '',
					});
					MobileMenuComponent.showToast(
						'E-Mail gesendet, bitte Posteingang prüfen.',
						'success'
					);
					break;
				}
				case 'register': {
					const values = MobileMenuComponent.readInputs(panel);
					const payload = {
						Email: values['Email'] ?? '',
						Password: values['Password'] ?? '',
						ConfirmPassword: values['ConfirmPassword'] ?? '',
						// Keep other fields default server-side for now
					};
					const res = await MobileMenuComponent.postJson('/Register', payload);
					if (!res.ok) {
						const data = await MobileMenuComponent.tryReadJson(res);
						throw new Error(data?.message || 'Registrierung fehlgeschlagen');
					}
					MobileMenuComponent.showToast(
						'Registrierung erfolgreich. Bitte E-Mail bestätigen.',
						'success'
					);
					// Optionally switch back to login
					MobileMenuComponent.switchToPanel('mobilemenu-client');
					break;
				}
			}
		} catch (err: any) {
			MobileMenuComponent.showToast(err?.message || 'Fehler', 'error');
		} finally {
			MobileMenuComponent.setBusy(actionEl, false);
		}
	};

	// Toast API
	private static toastRoot: HTMLElement | null = null;
	private static ensureToastRoot(): HTMLElement {
		if (
			MobileMenuComponent.toastRoot &&
			document.body.contains(MobileMenuComponent.toastRoot)
		) {
			return MobileMenuComponent.toastRoot;
		}
		const root = document.createElement('div');
		root.className = 'c-toasts';
		document.body.appendChild(root);
		MobileMenuComponent.toastRoot = root;
		return root;
	}

	private static showToast(
		message: string,
		type: 'success' | 'error' | 'info' = 'info',
		durationMs = 3000
	) {
		const root = MobileMenuComponent.ensureToastRoot();
		const toast = document.createElement('div');
		toast.className = `c-toast c-toast--${type}`;
		toast.textContent = message;
		root.appendChild(toast);
		requestAnimationFrame(() => toast.classList.add('is-visible'));
		const hide = () => {
			toast.classList.add('is-leaving');
			toast.classList.remove('is-visible');
			window.setTimeout(() => toast.remove(), 350);
		};
		const timer = window.setTimeout(hide, durationMs);
		toast.addEventListener('click', () => {
			window.clearTimeout(timer);
			hide();
		});
	}

	private static async tryReadJson(res: Response) {
		try {
			return await res.json();
		} catch {
			return undefined;
		}
	}

	private static switchToPanel(id: string) {
		const currentPanel = document.querySelector(
			'.c-mobilemenu.is-open'
		) as HTMLElement | null;
		const nextPanel = document.getElementById(id) as HTMLElement | null;
		if (!nextPanel) return;
		if (currentPanel) {
			currentPanel.classList.add('is-closing');
			currentPanel.classList.remove('is-open');
			const body = currentPanel.querySelector(
				'.c-mobilemenu__panel'
			) as HTMLElement | null;
			const duration = MobileMenuComponent.getTransitionMsFor(body);
			window.setTimeout(() => {
				currentPanel.classList.remove('is-closing');
				currentPanel.setAttribute('aria-hidden', 'true');
				currentPanel.hidden = true;
			}, duration + 50);
		}
		document.documentElement.style.overflow = 'hidden';
		nextPanel.classList.remove('is-closing');
		nextPanel.hidden = false;
		nextPanel.setAttribute('aria-hidden', 'false');
		void nextPanel.offsetWidth;
		nextPanel.classList.add('is-open');
	}

	private static closeOpenPanel() {
		const panel = document.querySelector(
			'.c-mobilemenu.is-open'
		) as HTMLElement | null;
		if (!panel) return;
		panel.classList.add('is-closing');
		panel.classList.remove('is-open');
		const body = panel.querySelector(
			'.c-mobilemenu__panel'
		) as HTMLElement | null;
		const duration = MobileMenuComponent.getTransitionMsFor(body);
		const finish = () => {
			// Return focus outside the panel before hiding so aria-hidden isn't set while a descendant has focus
			if (document.activeElement && panel.contains(document.activeElement)) {
				const opener = document.querySelector<HTMLElement>(`[aria-controls="${panel.id}"]`);
				(opener ?? document.body).focus();
			}
			panel.classList.remove('is-closing');
			panel.setAttribute('aria-hidden', 'true');
			panel.hidden = true;
			// unlock scroll when last panel closes
			document.documentElement.style.overflow = '';
			body?.removeEventListener('transitionend', onEnd);
		};
		const onEnd = (e: Event) => {
			if ((e as TransitionEvent).propertyName !== 'transform') return;
			finish();
		};
		body?.addEventListener('transitionend', onEnd);
		window.setTimeout(finish, duration + 50);
	}

	private static getTransitionMsFor(el: HTMLElement | null): number {
		if (!el) return 300;
		const styles = window.getComputedStyle(el);
		const dur = styles.transitionDuration || '0.25s';
		const first = dur.split(',')[0].trim();
		if (first.endsWith('ms')) return parseFloat(first);
		if (first.endsWith('s')) return parseFloat(first) * 1000;
		return 300;
	}

	private lockScroll(lock: boolean) {
		if (lock) {
			document.documentElement.style.overflow = 'hidden';
			(document.activeElement as HTMLElement)?.blur?.();
		} else {
			document.documentElement.style.overflow = '';
		}
	}

	private getTransitionMs(): number {
		// Read computed transition-duration on the sliding element
		const el = this.panelBody;
		if (!el) return 300;
		const styles = window.getComputedStyle(el);
		const dur = styles.transitionDuration || '0.25s';
		// Handle multiple durations like '0.25s, 0.25s'
		const first = dur.split(',')[0].trim();
		if (first.endsWith('ms')) return parseFloat(first);
		if (first.endsWith('s')) return parseFloat(first) * 1000;
		return 300;
	}

	private handleResize = () => {
		// crude check: if header nav becomes visible (>= xl), close menu
		if (window.innerWidth >= 1200 && this.open) {
			this.hide();
		}
	};
}
